import React, { useEffect, useState, useCallback, useContext } from 'react';
import appConfig from 'app-config';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FIELD_MANAGEMENT_GROUP_ID } from '../../../../override/constants/application-constants';
import { useModal } from '@jutro/components';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { UserService } from 'gw-capability-gateway';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { FormModalPopover } from 'ml-components-platform-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import { MLPolicyTypeUtil, MLProducerUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';

import mlGroupFinderDialogModal from './MLGroupFinderDialogModal';
import mlGroupFinderDialogModalMsg from './MLGroupFinderDialogModal.messages';

import metadata from './MLJobResultsFilterComponent.metadata.json5';
import messages from './MLJobResultsFilterComponent.messages';
import styles from './MLJobResultsFilterComponent.module.scss';

const HIDDEN_FIELDS_NON_IA_PCS_METGA = ['PRODUCER', 'GROUP'];
const DATE_RANGE = 'DATE_RANGE';
const PRODUCT = 'PRODUCT';
const GROUP = 'GROUP';
const PRODUCER = 'PRODUCER';

const MLJobResultsFilterComponent = (props) => {
    const modalApi = useModal();
    const {
        id,
        filterData,
        hideFields: initialHideFields,
        productCodes: initialProductCodes,
        onFilter,
        onClearFilters,
        dateLabel,
        resultsRowCount,
        additionalFiltersComponent,
        updateFilterData,
        additionalFilterProps,
        showTranscludeRow,
        showSeparator,
        minDate,
        maxDate
    } = props;
    const { MLUserTypes, gatewayParamConfig } = appConfig;
    const { getPolicyTypeName } = MLPolicyTypeUtil();
    const { getProducerCodeDisplayName } = MLProducerUtil();
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        clearBlockAlerts,
        addWarningAlert,
    } = MLNotificationBlockUtil();

    const { refreshData } = useDataRefresh();
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation(id);

    const [currentUser, updateCurrentUser] = useState(undefined);
    const [selectedGroup, updateSelectedGroup] = useState(undefined);
    const [hideFields, updateHideFields] = useState(initialHideFields || []);
    const [producerList, updateProducerList] = useState([]);

    const getProductDisplayName = useCallback((productCode) => {
        return getPolicyTypeName(productCode);
    }, [getPolicyTypeName]);

    const retrieveProducerCodeDisplayName = useCallback((producer) => {
        return getProducerCodeDisplayName(producer);
    }, [getProducerCodeDisplayName]);

    const getProductAvailableValues = useCallback(() => {
        const productCodes = initialProductCodes || [
            MLProductCodeUtil.getPersonalAutoCode(),
            MLProductCodeUtil.getPersonalUmbrellaCode(),
            MLProductCodeUtil.getHomeOwnersCode()
        ];

        return productCodes.map((product) => {
            return {
                code: product,
                name: getProductDisplayName(product)
            };
        });
    }, [getProductDisplayName, initialProductCodes]);

    const getProducerAvailableValues = useCallback(() => {
        const sortedProducerList = _.sortBy(producerList, 'producerContactLastName_ML');
        return sortedProducerList.map((producer) => {
            return {
                code: producer.code,
                name: retrieveProducerCodeDisplayName(producer)
            };
        });
    }, [retrieveProducerCodeDisplayName, producerList]);

    const isIAOrPCSOrMetGA = useCallback((initialUser) => {
        const user = initialUser || currentUser;
        if(user) {
            return user.userType === MLUserTypes.IA || user.userType === MLUserTypes.PCS
            || user.userType === MLUserTypes.MET_GA;
        }
        return false;
    }, [MLUserTypes.IA, MLUserTypes.MET_GA, MLUserTypes.PCS, currentUser]);

    const addHiddenFieldsBasedOnUserType = useCallback((user) => {
        // hide producer and groups filtering if not IA/PCS
        if (!isIAOrPCSOrMetGA(user)) {
            updateHideFields([...hideFields, ...HIDDEN_FIELDS_NON_IA_PCS_METGA]);
        }
    }, [hideFields, isIAOrPCSOrMetGA]);

    const populateAvailableProducerCodes = useCallback((producerCodes) => {
        filterData.producerCodes_ML = [];
        filterData.producerCodes_ML.push(...producerCodes);
        updateProducerList(filterData.producerCodes_ML);
        filterData.selectedProducerCode = undefined;
    }, [filterData]);

    const setAvailableProducerCodes = useCallback((initialAffiliatedGroupML = undefined) => {
        const selectedGroupId = _.get(initialAffiliatedGroupML, 'groupID') || _.get(selectedGroup, 'groupID');
        const selectedGroupPublicId = _.get(initialAffiliatedGroupML, 'publicID') || _.get(selectedGroup, 'publicID');
        // Skip fetching producer codes for the field management group
        // Field Management group has more than 40k Producer Codes,
        //  so we skip fetching them to avoid timeout errors
        if (selectedGroupPublicId && selectedGroupId !== FIELD_MANAGEMENT_GROUP_ID) {
            const selectedGroupPromise = UserService.getProducerCodesForGroup(selectedGroupPublicId, authHeader);
            selectedGroupPromise.then((selectedProducerCodes) => {
                populateAvailableProducerCodes(selectedProducerCodes);
            });
        }
    }, [authHeader, populateAvailableProducerCodes, selectedGroup]);

    const initProducers = useCallback((affiliatedGroupML, user) => {
        if (isIAOrPCSOrMetGA(user)) {
            setAvailableProducerCodes(affiliatedGroupML);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const initCurrentUser = useCallback(() => {
        UserService.getCurrentUser(authHeader).then((user) => {
            updateCurrentUser(user);
            updateSelectedGroup(user.affiliatedGroup_ML);
            addHiddenFieldsBasedOnUserType(user);
            initProducers(user.affiliatedGroup_ML, user);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const setSearchProducerCodes = (producerCode) => {
            if (_.isUndefined(filterData.selectedProducerCode)) {
                return ;
            }
            if (!producerCode) {
                // if producerCode selected is all producer code, set producerCodes_ML back to producerList to remove specific producerCodes_ML filter
                filterData.producerCodes_ML = producerList;
            } else {
                const producer = producerList.find((prod) => prod.code === producerCode);
                filterData.producerCodes_ML = producer ? [producer] : [];
            }
        };

        setSearchProducerCodes(filterData.selectedProducerCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterData.selectedProducerCode]);

    useEffect(() => {
        const checkForMaxResultsMessage = (count) => {
            clearBlockAlerts('max_search_results_message');
            if (!Number.isNaN(count) && count >= gatewayParamConfig.filteredActivityReportsMaxResults) {
                const errorMsg = (
                    <span role="alert" aria-live="polite">
                    <FormattedMessage
                        {...messages.multipleRecordsAvailableAlert}
                        values={{
                            multipleRecordsAreAvailable: (<strong>{translator(messages.multipleRecordsAreAvailable)}</strong>),
                            numRows: gatewayParamConfig.filteredActivityReportsMaxResults
                        }}
                    />
                    </span>
                );
                addWarningAlert(errorMsg, 'max_search_results_message');
            }
        };

        checkForMaxResultsMessage(resultsRowCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resultsRowCount]);

    const isValidIAorPCSorMetGAAgent = () => {
        return currentUser ?
            currentUser.userType === MLUserTypes.IA || currentUser.userType === MLUserTypes.PCS || currentUser.userType === MLUserTypes.MET_GA
            : false;
    };

    const getDateLabel = () => {
        return dateLabel || translator(messages.dateRangeLabel);
    };

    const shouldShow = (fieldName) => {
        return hideFields.indexOf(fieldName) === -1;
    };

    const clearFilters = () => {
        // For Payment Log, need to keep selected group publidID to retrieve producerCodes from PC
        const selectedGroupPublicID = filterData.groupPublicID_ML;
        /**
         * Need to clear the object, but can't reassign as it will disconnect
         * from the parent.
         */
        for (const prop of Object.getOwnPropertyNames(filterData)) {
            delete filterData[prop];
        }
        if(selectedGroupPublicID) {
            filterData.groupPublicID_ML = selectedGroupPublicID
        }
        // selectedProducer = null;
        onClearFilters();
        clearBlockAlerts('max_search_results_message');
    };

    const writeValue = useCallback((value, path) => {
        _.set(filterData, path, value);
        updateFilterData(filterData);
        refreshData();
    }, [filterData, refreshData, updateFilterData]);

    const onEditGroupClick = () => {
        const componentProps = {
            title: translator(mlGroupFinderDialogModalMsg.findAGroupTitle),
            buttonType: 'primary',
            CustomComponent: mlGroupFinderDialogModal,
            additionalPEStyle: 'customFooterModal',
            viewModelService,
            overrideProps: {
                updateSelectedGroup,
                populateAvailableProducerCodes,
                authHeader,
                filterData,
                writeFilterData: writeValue
            }
        };

        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        );
    };

    const getMaxFromDate = () => {
        if(filterData.toDate_ML) {
            return new Date(filterData.toDate_ML.year, filterData.toDate_ML.month, filterData.toDate_ML.day);
        }
        return maxDate;
    };

    const getMinToDate = () => {
        if(filterData.fromDate_ML) {
            return new Date(filterData.fromDate_ML.year, filterData.fromDate_ML.month, filterData.fromDate_ML.day);
        }
        return minDate;
    };

    const overrideProps = {
        separator: {
            visible: showSeparator === true ? true : !isValidIAorPCSorMetGAAgent()
        },
        dateLabelContainer: {
            visible: shouldShow(DATE_RANGE),
            content: getDateLabel()
        },
        dateRangeContainer: {
            visible: shouldShow(DATE_RANGE)
        },
        productContainer: {
            visible: shouldShow(PRODUCT)
        },
        productField: {
            availableValues: getProductAvailableValues(),
            value: _.get(filterData, 'policyType_ML')
        },
        groupContainer: {
            visible: shouldShow(GROUP)
        },
        selectedGroupName: {
            content: _.capitalize(_.get(selectedGroup, 'groupName'))
        },
        producerContainer: {
            visible: shouldShow(PRODUCER)
        },
        producerField: {
            availableValues: getProducerAvailableValues(),
            disabled: producerList.length === 0
        },
        transcludeRow: {
            visible: !!additionalFiltersComponent && showTranscludeRow
        },
        additionalFiltersComponent: {
            filterData,
            updateFilterData,
            ...additionalFilterProps
        },
        dateFromField: {
            minDate,
            maxDate: getMaxFromDate()
        },
        dateToField: {
            minDate: getMinToDate(),
            maxDate
        }
        

    };

    const resolvers = {
        resolveCallbackMap: {
            clearFilters,
            onEditGroupClick,
            onFilter
        },
        resolveComponentMap: {
            MLAlertGroupComponent: MLAlertGroupComponent,
            additionalFiltersComponent: additionalFiltersComponent
        }
    }

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            model={filterData}
            onValueChange={writeValue}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={styles}
        />
    );
};

MLJobResultsFilterComponent.propTypes = {
    dateLabel: PropTypes.string,
    productCodes: PropTypes.array,
    filterData: PropTypes.shape({}).isRequired,
    hideFields: PropTypes.array,
    onFilter: PropTypes.func.isRequired,
    onClearFilters: PropTypes.func.isRequired,
    resultsRowCount: PropTypes.number.isRequired,
    updateFilterData: PropTypes.func.isRequired,
    additionalFilterProps: PropTypes.shape({}),
    showTranscludeRow: PropTypes.bool,
    showSeparator: PropTypes.bool
};

MLJobResultsFilterComponent.defaultProps = {
    dateLabel: undefined,
    productCodes: undefined,
    hideFields: undefined,
    additionalFilterProps: undefined,
    showTranscludeRow: true
}

export default MLJobResultsFilterComponent;
